import type { Id } from '@/convex/_generated/dataModel';
import type { RealtimeVoiceToolContext } from './realtime-voice-tool-context';
import { answerCurrentQuestionSchema } from '@/lib/voice-call-tool-input';
import { getRealtimeVoiceAnswerText } from '@/lib/realtime-voice-questionnaire';
import type { z } from 'zod';

/** Source-owned dispatch authorization stays on the server for reads and answers. */
export async function answerRealtimeVoiceDispatchQuestion(
  context: RealtimeVoiceToolContext,
  parsed: z.infer<typeof answerCurrentQuestionSchema>,
  dispatchId?: string
): Promise<unknown> {
  const finish = (result: string, silent = false) =>
    context.finishRealtimeToolResult({
      toolName: 'answer_current_question',
      result,
      silent,
    });
  if (!context.answerDispatchQuestion || !context.getOffPageQuestion)
    return finish(context.askQuestionCopy.voiceNoPendingQuestion);
  const { pendingQuestion } = await context.getOffPageQuestion({
    orgThreadId: context.orgThreadId,
    sessionId: context.leaseSessionId,
    ...(dispatchId ? { dispatchId: dispatchId as Id<'threadDispatches'> } : {}),
  });
  if (!context.isCurrentCall())
    return 'Internal safety: this voice call is no longer active. Stay silent.';
  if (
    pendingQuestion?.kind === 'open-chat' ||
    parsed.groupAnswers ||
    parsed.skipCurrentQuestion ||
    parsed.questionnaireNavigation
  )
    return finish(
      JSON.stringify({
        status: 'open_destination',
        dispatchId,
        scope: dispatchId ? 'destination' : 'source',
        ...(!dispatchId ? { orgThreadId: context.orgThreadId } : {}),
        ...(dispatchId &&
        context.voiceDestinationRef?.current?.dispatchId === dispatchId
          ? { targetHref: context.voiceDestinationRef.current.targetHref }
          : {}),
        reason: 'question_requires_chat',
      })
    );
  const focus = context.voiceQuestionRef?.current;
  const questionRevision =
    parsed.questionRevision ??
    (focus?.dispatchId === dispatchId ? focus?.questionRevision : undefined);
  const toolCallId =
    parsed.toolCallId ??
    (focus?.dispatchId === dispatchId ? focus?.toolCallId : undefined);
  if (
    !pendingQuestion ||
    (pendingQuestion.questionRevision && !questionRevision) ||
    (toolCallId && toolCallId !== pendingQuestion.toolCallId) ||
    (questionRevision &&
      pendingQuestion.questionRevision !== questionRevision) ||
    (parsed.toolCallId && parsed.toolCallId !== pendingQuestion.toolCallId)
  )
    return finish(context.askQuestionCopy.voiceNoPendingQuestion);
  const answerText = getRealtimeVoiceAnswerText(parsed);
  if (!answerText)
    return finish(context.askQuestionCopy.voiceNoPendingQuestion);
  const result = await context.answerDispatchQuestion({
    orgThreadId: context.orgThreadId,
    sessionId: context.leaseSessionId,
    ...(dispatchId ? { dispatchId: dispatchId as Id<'threadDispatches'> } : {}),
    toolCallId: pendingQuestion.toolCallId,
    ...(questionRevision ? { questionRevision } : {}),
    answerText,
    ...(parsed.answerValue ? { answerValue: parsed.answerValue } : {}),
  });
  if (!context.isCurrentCall())
    return 'Internal safety: this voice call is no longer active. Stay silent.';
  if (result.success && context.voiceQuestionRef)
    context.voiceQuestionRef.current = null;
  return finish(
    result.success
      ? context.askQuestionCopy.voiceAnswerResumedSilent
      : context.askQuestionCopy.voiceNoPendingQuestion,
    result.success
  );
}
